import { fmtDuration, fmtEth, fmtTokens, type AgentView, type PublicConfig } from '@quintessence/core';
import { Link } from 'react-router-dom';
import { Sparkline } from '../charts/Sparkline';
import { sym } from '../lib';
import { SERIES } from '../theme';
import { AgentAvatar } from './AgentAvatar';
import { InstinctMeter } from './BrainReadout';
import { Icon } from './icons';

function roleStat(a: AgentView, symbol: string): [string, string] {
  const x = a.vault?.extra ?? {};
  switch (a.id) {
    case 'pyre':
      return ['Burned', `${fmtTokens(x.totalBurned ?? 0)} ${symbol}`];
    case 'hoard':
      return ['Reserve', `${fmtTokens(a.vault?.tokenBalance ?? 0)} ${symbol}`];
    case 'rain':
      return ['Airdropped', `${fmtTokens(x.totalDistributed ?? 0)} ${symbol}`];
    case 'tide':
      return ['ETH in LP', `${fmtEth(x.ethToLiquidity ?? 0)}`];
  }
}

export function AgentPanel({ agent, now, config }: { agent: AgentView; now: number; config: PublicConfig }) {
  const b = agent.brain;
  const [statLabel, statValue] = roleStat(agent, sym(config));
  const next = agent.nextDecisionAt - now;
  const color = SERIES[agent.id];
  return (
    <article className="agent-panel panel" style={{ ['--agent' as string]: color }}>
      <header className="ap-head">
        <AgentAvatar id={agent.id} size={46} />
        <div className="ap-title">
          <h3>{agent.persona.name}</h3>
          <span className="muted">{agent.persona.title}</span>
        </div>
        <div className="ap-iq" title="Brain IQ: grows with experience, prediction skill and calibration">
          <span className="ap-iq-value">{b.iq}</span>
          <span className="muted">IQ</span>
        </div>
      </header>

      <div className="ap-stats">
        <div>
          <span className="k">Vault</span>
          <span className="v">{fmtEth(agent.vault?.ethBalance ?? 0)} ETH</span>
        </div>
        <div>
          <span className="k">Spent</span>
          <span className="v">{fmtEth(agent.vault?.totalEthSpent ?? 0)} ETH</span>
        </div>
        <div>
          <span className="k">{statLabel}</span>
          <span className="v">{statValue}</span>
        </div>
        <div>
          <span className="k">Actions</span>
          <span className="v">{(agent.vault?.actionCount ?? 0).toLocaleString()}</span>
        </div>
        <div>
          <span className="k">Generation</span>
          <span className="v">{b.generation}</span>
        </div>
        <div>
          <span className="k">Memories</span>
          <span className="v">{b.experiences.toLocaleString()}</span>
        </div>
      </div>

      <div className="ap-sparks">
        <div>
          <span className="k">IQ</span>
          <Sparkline values={b.iqHistory} color={color} label={`${agent.persona.name} IQ trend`} />
        </div>
        <div>
          <span className="k">Reward (EMA) {b.rewardEma >= 0 ? '+' : '−'}{Math.abs(b.rewardEma).toFixed(2)}</span>
          <Sparkline values={b.rewardHistory} color={color} label={`${agent.persona.name} reward trend`} />
        </div>
      </div>

      <div className="ap-meters">
        <div className="mini-kv">
          <span>Confidence</span>
          <strong>{Math.round(b.confidence * 100)}%</strong>
        </div>
        <div className="mini-kv">
          <span>Accuracy</span>
          <strong>{Math.round(b.directionalAccuracy * 100)}%</strong>
        </div>
        <div className="mini-kv">
          <span>Exploration</span>
          <strong>{(b.exploration * 100).toFixed(1)}%</strong>
        </div>
      </div>
      <InstinctMeter weight={b.instinctWeight} color={color} />

      <blockquote className="ap-thought">
        <span className="muted">
          Last thought · {agent.lastThought ? agent.lastThought.size.toLowerCase() : 'none yet'}
        </span>
        <p>{agent.lastThought?.summary ?? 'Observing the market…'}</p>
      </blockquote>

      <footer className="ap-foot">
        <span>
          <Icon name="clock" size={14} /> {next > 0 ? `Next decision in ${fmtDuration(next)}` : 'Deciding now'}
          {agent.role.randomCadence && <span title="PYRE decides on a random clock so burns cannot be front-run"> · random</span>}
        </span>
        <Link to={`/agent/${agent.id}`} className="link-arrow">
          Brain <Icon name="arrow" size={14} />
        </Link>
      </footer>
    </article>
  );
}
